import React, { useCallback, useEffect, useState } from 'react';
import { NumberInput, Select } from '@mantine/core';
import { useAppDispatch } from '@hooks/useDispatch';
import { setEditingCell } from '@store/Spreadsheet/editingSlice';
import { updateRow } from '@store/Spreadsheet/rowsSlice';
import { CommonCellProps } from '../Cell';
import classes from '../Cell.module.css';
import { cellValueChanged } from '@store/Spreadsheet/cellChangesSlice';

interface CellNumberUnitProps extends CommonCellProps {
  unit?: string;
}

export default function CellNumberUnit({ rowId, columnId, isEditing, value, currentRow, options = [], unit }: CellNumberUnitProps ) {
  const dispatch = useAppDispatch();
  const defaultUnit = unit || options[0];
  // Parse initial value
  const parsedValue = typeof value === 'string' ? value.split( ' ' ) : [ value, defaultUnit ];
  const [ localValue, setLocalValue ] = useState<string>( parsedValue[0] ?? '' );
  const [ selectedUnit, setSelectedUnit ] = useState<string>( parsedValue[1] ?? defaultUnit );

  useEffect(() => {
    const newParsedValue = typeof value === 'string' ? value.split( ' ' ) : [ value, defaultUnit ];
    setLocalValue( newParsedValue[0] ?? '' );
    setSelectedUnit( newParsedValue[1] ?? defaultUnit );
  }, [ value, defaultUnit ]);

  const commitEdit = useCallback(() => {
    if ( !currentRow ) return;

    // Only append a real unit. A column without units leaves `selectedUnit`
    // undefined, and `${localValue} ${undefined}` would persist "11 undefined".
    const trimmedUnit = selectedUnit?.trim();
    const newValue = trimmedUnit ? `${localValue} ${trimmedUnit}` : `${localValue}`;
    const newValues = { ...currentRow.values, [columnId]: newValue };

    dispatch( updateRow({ id: rowId, changes: { values: newValues }}));
    dispatch( cellValueChanged({ rowId, columnId, oldValue: currentRow.values[columnId], newValue }));
    dispatch( setEditingCell( null ));
  }, [ dispatch, rowId, columnId, currentRow, localValue, selectedUnit ]);

  const cancelEdit = useCallback(() => {
    dispatch( setEditingCell( null ));
  }, [dispatch]);

  const handleKeyDown = useCallback(( e: React.KeyboardEvent<HTMLInputElement> ) => {
    if ( !isEditing ) return;
    if ( e.key === 'Enter' ) {
      commitEdit();
    } else if ( e.key === 'Escape' ) {
      cancelEdit();
    }
  }, [ isEditing, commitEdit, cancelEdit ]);

  if ( !isEditing ) {
    return <span className={classes.cellValue}>{value}</span>;
  }

  return (
    <div className={classes.cellNumberUnitEdit}>
      <NumberInput
        className={classes.cellNumberUnitInput}
        value={localValue}
        onChange={( value ) => setLocalValue( value.toString())}
        onKeyDown={handleKeyDown}
        onBlur={commitEdit}
        autoFocus
        size="xs"
        variant="unstyled"
        hideControls
      />
      <Select
        className={classes.cellNumberUnitSelect}
        value={selectedUnit}
        onChange={( newUnit ) => newUnit && setSelectedUnit( newUnit )}
        data={options}
        onBlur={commitEdit}
        dropdownOpened={true}
        withCheckIcon={false}
        size="xs"
        variant="unstyled"
      />
    </div>
  );
}
